// components/modal/ImportPppoeUser.jsx
import { useState, useRef } from "react";
import {
    FaXmark,
    FaCloudArrowUp,
    FaFileExcel,
    FaSpinner,
    FaCircleInfo,
} from "react-icons/fa6";
import { PppoeUserService } from "../../../../../services/PppoeUserService";
import { FaCheckCircle } from "react-icons/fa";

export const ImportPppoeUser = ({ openModal, setOpenModal, refresh }) => {
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    /* ---------- Reset State ---------- */
    const resetState = () => {
        setFile(null);
        setDragActive(false);
        setLoading(false);
        setResult(null);
        setError(null);
    };

    const handleClose = () => {
        resetState();
        setOpenModal(false);
    };

    /* ---------- Drag & Drop ---------- */
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) {
            validateAndSetFile(droppedFile);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            validateAndSetFile(selectedFile);
        }
    };

    const validateAndSetFile = (f) => {
        const allowed = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "text/csv",
        ];
        const allowedExt = [".xlsx", ".xls", ".csv"];
        const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();

        if (!allowed.includes(f.type) && !allowedExt.includes(ext)) {
            setError("Format file tidak didukung. Gunakan .xlsx, .xls, atau .csv");
            setFile(null);
            return;
        }

        if (f.size > 10 * 1024 * 1024) {
            setError("Ukuran file maksimal 10MB");
            setFile(null);
            return;
        }

        setError(null);
        setFile(f);
    };

    const removeFile = () => {
        setFile(null);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    /* ---------- Submit Import ---------- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return;

        setLoading(true);
        setError(null);

        try {
            const res = await PppoeUserService.import({ file: file });
            setResult(res.data);

            // Optional: polling progress setelah sukses
            // startPolling(res.data.group_id);

            if (refresh) refresh();
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Gagal mengupload file";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    /* ---------- Format ukuran file ---------- */
    const formatSize = (bytes) => {
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    };

    if (!openModal) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div
                className="relative w-full max-w-lg rounded-2xl shadow-2xl"
                style={{
                    background: "linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(15, 23, 42, 1))",
                    border: "1px solid rgba(51, 65, 85, 0.5)",
                }}
            >
                {/* ── Header ── */}
                <div
                    className="flex items-center justify-between px-6 py-4"
                    style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}
                >
                    <div>
                        <h3 className="text-base font-bold text-gray-100">
                            Import Data PPPoE User
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Upload file Excel (.xlsx / .xls) atau CSV
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg text-slate-400 hover:text-gray-100 hover:bg-slate-700/60 transition-colors"
                    >
                        <FaXmark className="text-sm" />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="px-6 py-5">
                    {/* ====== State: Sukses ====== */}
                    {result ? (
                        <div className="text-center py-4">
                            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                                <FaCheckCircle className="text-3xl text-emerald-400" />
                            </div>
                            <h4 className="text-lg font-bold text-gray-100 mb-1">
                                Import Berhasil Dijadwalkan
                            </h4>
                            <p className="text-sm text-slate-400 mb-6">
                                File sedang diproses di background. Anda bisa menutup halaman ini.
                            </p>

                            <div
                                className="rounded-xl p-4 mb-6 text-left space-y-3"
                                style={{
                                    background: "rgba(15, 23, 42, 0.6)",
                                    border: "1px solid rgba(51, 65, 85, 0.3)",
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                                        Kode Import
                                    </span>
                                    <span className="font-mono text-sm text-cyan-400 font-bold">
                                        {result.code}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                                        Group ID
                                    </span>
                                    <span className="font-mono text-sm text-slate-300">
                                        {result.group_id}
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={handleClose}
                                className="w-full py-2.5 rounded-xl text-sm font-semibold bg-cyan-600 hover:bg-cyan-500 text-white transition-colors"
                            >
                                Tutup
                            </button>
                        </div>
                    ) : (
                        /* ====== State: Form Upload ====== */
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Drop Zone */}
                            <div
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`
                  relative rounded-xl p-6 text-center cursor-pointer transition-all duration-200
                  ${dragActive
                                        ? "border-cyan-500/60 bg-cyan-500/5"
                                        : file
                                            ? "border-emerald-500/30 bg-emerald-500/3"
                                            : "border-slate-600/40 bg-slate-800/30 hover:border-slate-500/50 hover:bg-slate-800/50"
                                    }
                `}
                                style={{ border: "1px dashed" }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />

                                {!file ? (
                                    <div className="space-y-3">
                                        <div
                                            className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto transition-colors ${dragActive ? "bg-cyan-500/10" : "bg-slate-700/40"
                                                }`}
                                        >
                                            <FaCloudArrowUp
                                                className={`text-2xl transition-colors ${dragActive ? "text-cyan-400" : "text-slate-500"
                                                    }`}
                                            />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-300">
                                                {dragActive
                                                    ? "Lepaskan file di sini..."
                                                    : "Drag & drop file atau klik untuk pilih"}
                                            </p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                Format: .xlsx, .xls, .csv (maks. 10MB)
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                            <FaFileExcel className="text-xl text-emerald-400" />
                                        </div>
                                        <div className="flex-1 text-left min-w-0">
                                            <p className="text-sm font-medium text-gray-100 truncate">
                                                {file.name}
                                            </p>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                {formatSize(file.size)}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile();
                                            }}
                                            className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                                        >
                                            <FaXmark className="text-sm" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Error */}
                            {error && (
                                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-500/8 border border-red-500/20">
                                    <FaCircleInfo className="text-red-400 text-sm mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-400">{error}</p>
                                </div>
                            )}

                            {/* Info Kolom Wajib */}
                            <div
                                className="rounded-xl p-4"
                                style={{
                                    background: "rgba(0, 180, 216, 0.04)",
                                    border: "1px solid rgba(0, 180, 216, 0.12)",
                                }}
                            >
                                <p className="text-xs font-semibold text-cyan-400/80 uppercase tracking-wider mb-2">
                                    Kolom Wajib
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        "id_pelanggan",
                                        "nama",
                                        "username",
                                        "password",
                                        "profil",
                                    ].map((col) => (
                                        <span
                                            key={col}
                                            className="px-2.5 py-1 rounded-lg text-xs font-mono bg-cyan-500/8 text-cyan-400/90 border border-cyan-500/15"
                                        >
                                            {col}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-[11px] text-slate-500 mt-2.5 leading-relaxed">
                                    Kolom opsional: <span className="font-mono text-slate-400">no_telepon, nas, pop, odp, odc, reseller, status, tipe, latitude, longitude, alamat</span>
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-slate-300 transition-colors"
                                    style={{
                                        background: "rgba(51, 65, 85, 0.3)",
                                        border: "1px solid rgba(51, 65, 85, 0.4)",
                                    }}
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    disabled={!file || loading}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? (
                                        <>
                                            <FaSpinner className="animate-spin text-sm" />
                                            Mengupload...
                                        </>
                                    ) : (
                                        <>
                                            <FaCloudArrowUp className="text-sm" />
                                            Import
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};